// src/audio/AudioManager.js
import ButtonPressSound from "../sounds/button-press.mp3";
import BellSound from "../sounds/alarm-bell.mp3";
import DigitalSound from "../sounds/alarm-digital.mp3";
import KitchenSound from "../sounds/alarm-kitchen.mp3";
import TickingSlowSound from "../sounds/ticking-slow.mp3";

/**
 * Lightweight singleton AudioManager.
 * - Uses HTMLAudioElement (new Audio(src))
 * - Keeps instances per sound name so we can control stop/load/volume
 * - Methods return promises where appropriate (play)
 *
 * Sound registry keys:
 *  - "button"  -> button press sound
 *  - "Bell"    -> bell alarm (sound names matching select options)
 *  - "Digital" -> digital alarm
 *  - "Kitchen" -> kitchen alarm
 *  - "alarm"   -> fallback alarm (alias to Bell)
 */

const SOUND_REGISTRY = {
  button: ButtonPressSound,
  Bell: BellSound,
  Digital: DigitalSound,
  Kitchen: KitchenSound,
  alarm: BellSound,
  tick: TickingSlowSound,
};

class AudioManager {
  constructor() {
    this.instances = new Map(); // name => HTMLAudioElement
    this.muted = false;
    this.globalVolume = 1;
  }

  _createInstance(name) {
    const url = SOUND_REGISTRY[name];
    if (!url) return null;
    const a = new Audio(url);
    a.preload = "auto";
    a.volume = this.globalVolume;
    a.loop = false;
    return a;
  }

  load(name) {
    if (this.instances.has(name)) return this.instances.get(name);
    const instance = this._createInstance(name);
    if (!instance) return null;
    this.instances.set(name, instance);
    return instance;
  }

  async play(name, { loop = false, volume } = {}) {
    if (this.muted) return Promise.resolve();
    let instance = this.instances.get(name) || this._createInstance(name);
    if (!instance) return Promise.resolve();

    // store instance if not already stored
    if (!this.instances.has(name)) this.instances.set(name, instance);

    instance.loop = !!loop;
    if (typeof volume === "number") {
      instance.volume = Math.max(0, Math.min(1, volume));
    }

    try {
      const p = instance.play();

      // Normalize:
      // - If play() returns a promise, attach a catch so rejections are swallowed.
      // - If it returns undefined (non-promise), resolve immediately.
      if (p && typeof p.then === "function") {
        return p.catch(() => undefined);
      }
      return Promise.resolve();
    } catch (err) {
      // Swallow synchronous errors and resolve
      return Promise.resolve();
    }
  }

  stop(name) {
    const instance = this.instances.get(name);
    if (!instance) return;
    try {
      instance.pause();
      instance.currentTime = 0;
      instance.loop = false;
    } catch {
      // ignore
    }
  }

  setVolume(name, v) {
    const instance = this.instances.get(name);
    if (!instance) return;
    instance.volume = Math.max(0, Math.min(1, Number(v) || 0));
  }

  mute() {
    this.muted = true;
    // optionally pause all sounds
    for (const inst of this.instances.values()) {
      try {
        inst.pause();
      } catch {}
    }
  }

  unmute() {
    this.muted = false;
  }

  isMuted() {
    return this.muted;
  }

  // expose registry keys (useful for tests)
  getAvailableSounds() {
    return Object.keys(SOUND_REGISTRY);
  }
}

// assign to a named variable before exporting to satisfy ESLint import/no-anonymous-default-export
/** @type {AudioManager} */
const audioManager = new AudioManager();

export default audioManager;
